import { CapaDeModal } from '@/shared/components/CapaDeModal'
import React, { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { estiloDeBoton } from "@/shared/lib/estilosDeBoton";

interface AddCommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (comment: string) => Promise<void>;
  orderNumber: string;
}

export const AddCommentModal: React.FC<AddCommentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  orderNumber,
}) => {
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!comment.trim()) {
      setError("El comentario no puede estar vacío");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await onSubmit(comment.trim());
      setComment("");
      onClose();
    } catch {
      setError("Error al agregar el comentario. Intenta nuevamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setComment("");
      setError("");
      onClose();
    }
  };

  return (
    <CapaDeModal>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Agregar comentario
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Orden {orderNumber}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Comentario
            </label>
            <textarea
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
                setError("");
              }}
              placeholder="Escribe tu comentario aquí..."
              rows={5}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 resize-none"
            />
            {error && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                {error}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 pt-0">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className={estiloDeBoton("secundario")}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !comment.trim()}
              className={estiloDeBoton("primario")}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                "Guardar comentario"
              )}
            </button>
          </div>
        </form>
      </div>
    </CapaDeModal>
  );
};
